<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  userLocation: {
    type: String,
    default: 'London'
  }
});

const weatherData = ref(null);
const loading = ref(false);
const errorMessage = ref('');

const getWeatherIcon = (code) => {
  if (code === 0) return { icon: 'bi-sun-fill', text: 'Clear', colorClass: 'icon-sun' };
  if ([1, 2].includes(code)) return { icon: 'bi-cloud-sun-fill', text: 'Partly Cloudy', colorClass: 'icon-partly-cloudy' };
  if (code === 3) return { icon: 'bi-cloud-fill', text: 'Overcast', colorClass: 'icon-cloud' };
  if ([45, 48].includes(code)) return { icon: 'bi-cloud-fog2-fill', text: 'Foggy', colorClass: 'icon-fog' };
  if ([51, 53, 55, 56, 57].includes(code)) return { icon: 'bi-cloud-drizzle-fill', text: 'Drizzle', colorClass: 'icon-rain' };
  if ([61, 63, 65, 66, 67].includes(code)) return { icon: 'bi-cloud-rain-fill', text: 'Rain', colorClass: 'icon-rain' };
  if ([71, 73, 75, 77].includes(code)) return { icon: 'bi-snowflake', text: 'Snow', colorClass: 'icon-snow' };
  if ([80, 81, 82].includes(code)) return { icon: 'bi-cloud-rain-heavy-fill', text: 'Showers', colorClass: 'icon-rain' };
  if ([85, 86].includes(code)) return { icon: 'bi-cloud-snow-fill', text: 'Snow Showers', colorClass: 'icon-snow' };
  if ([95, 96, 99].includes(code)) return { icon: 'bi-cloud-lightning-rain-fill', text: 'Thunderstorm', colorClass: 'icon-thunder' };
  return { icon: 'bi-cloud-fill', text: 'Unknown', colorClass: 'icon-cloud' };
};

const formatDay = (dateString, index) => {
  if (index === 0) return 'Today';
  return new Date(dateString).toLocaleDateString('en-GB', { weekday: 'short' });
};

const fetchUKWeather = async () => {
  const input = props.userLocation ? props.userLocation.trim() : '';
  if (!input) return;

  loading.value = true;
  errorMessage.value = '';

  try {
    let lat, lon, displayName;
    const isPostcode = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i.test(input);

    if (isPostcode) {
      const pcRes = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(input)}`);
      const pcData = await pcRes.json();
      if (pcData.status !== 200) throw new Error('Invalid UK Postcode');
      lat = pcData.result.latitude;
      lon = pcData.result.longitude;
      displayName = `${pcData.result.outward_code}, ${pcData.result.admin_district}`;
    } else {
      const geoRes = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(input)}&count=5&language=en&format=json`);
      const geoData = await geoRes.json();
      if (!geoData.results || geoData.results.length === 0) throw new Error('Town or location not found');

      const ukMatch = geoData.results.find(place => place.country_code === 'GB') || geoData.results[0];
      lat = ukMatch.latitude;
      lon = ukMatch.longitude;
      displayName = `${ukMatch.name}${ukMatch.admin1 ? ', ' + ukMatch.admin1 : ''}`;
    }

    // Request 10 days forecast parameter instead of 5
    const weatherRes = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,relative_humidity_2m,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max&forecast_days=10&timezone=auto`
    );
    if (!weatherRes.ok) throw new Error('Failed to fetch weather data');
    
    const data = await weatherRes.json();

    if (!data.current || !data.daily) {
      throw new Error('Weather data unavailable');
    }

    weatherData.value = {
      location: displayName,
      currentTemp: Math.round(data.current.temperature_2m),
      humidity: data.current.relative_humidity_2m ?? null,
      windSpeed: Math.round(data.current.wind_speed_10m ?? 0),
      rainChance: data.daily.precipitation_probability_max ? data.daily.precipitation_probability_max[0] : 0,
      currentIcon: getWeatherIcon(data.current.weather_code),
      forecast: data.daily.time.slice(0, 10).map((time, i) => ({
        day: formatDay(time, i),
        maxTemp: Math.round(data.daily.temperature_2m_max[i]),
        minTemp: Math.round(data.daily.temperature_2m_min[i]),
        icon: getWeatherIcon(data.daily.weather_code[i])
      }))
    };
  } catch (err) {
    weatherData.value = null;
    errorMessage.value = err.message || 'Error fetching weather data';
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.userLocation,
  (newVal) => {
    if (newVal) fetchUKWeather();
  },
  { immediate: true }
);
</script>

<template>
  <div class="weather-card">
    <div v-if="loading" class="state-message">Loading weather...</div>
    <div v-else-if="errorMessage" class="error-message">{{ errorMessage }}</div>

    <template v-else-if="weatherData">
      <!-- Header -->
      <h2 class="location">{{ weatherData.location }}</h2>
      
      <!-- Inner Hero Display -->
      <div class="hero-weather-container">
        <div class="current-display">
          <i :class="['bi', weatherData.currentIcon.icon, weatherData.currentIcon.colorClass]"></i>
          <span class="temp">{{ weatherData.currentTemp }}°C</span>
        </div>

        <div class="weather-metrics">
          <div class="metric-item" title="Rain Probability">
            <i class="bi bi-droplet-fill metric-icon rain"></i>
            <span>{{ weatherData.rainChance }}%</span>
          </div>
          <span class="metric-divider">•</span>
          <div class="metric-item" title="Humidity">
            <i class="bi bi-water metric-icon humidity"></i>
            <span>{{ weatherData.humidity }}%</span>
          </div>
          <span class="metric-divider">•</span>
          <div class="metric-item" title="Wind Speed">
            <i class="bi bi-wind metric-icon wind"></i>
            <span>{{ weatherData.windSpeed }} km/h</span>
          </div>
        </div>
      </div>

      <!-- 10-Day 2-Row Forecast Grid -->
      <div class="forecast-section">
        <div class="forecast-title">10-Day Forecast</div>
        <div class="forecast-grid">
          <div 
            v-for="(day, index) in weatherData.forecast" 
            :key="index" 
            class="forecast-day"
          >
            <span class="day-name">{{ day.day }}</span>
            <i :class="['bi', day.icon.icon, day.icon.colorClass]" :title="day.icon.text"></i>
            <span class="temps">
              {{ day.maxTemp }}° <span class="temp-min">{{ day.minTemp }}°</span>
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.weather-card {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;

  .state-message { color: #64748b; margin: auto 0; }
  .error-message { color: #e11d48; margin: auto 0; }

  .location {
    font-size: 1.15rem;
    color: #d97706;
    margin: 0;
    font-weight: 700;
  }

  .hero-weather-container {
    width: 100%;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 0.85rem 0.75rem;
    margin: 0.5rem 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;

    .current-display {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;

      i {
        font-size: 2.5rem;
        line-height: 1;
      }

      .temp {
        font-size: 2.5rem;
        font-weight: 800;
        color: #0f172a;
        letter-spacing: -1px;
        line-height: 1;
        font-variant-numeric: tabular-nums;
      }
    }

    .weather-metrics {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      padding: 3px 12px;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      color: #475569;

      .metric-item {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .metric-icon {
        font-size: 0.8rem;
        &.rain { color: #0284c7; }
        &.humidity { color: #2563eb; }
        &.wind { color: #64748b; }
      }

      .metric-divider {
        color: #cbd5e1;
      }
    }
  }

  /* Forecast Section configured for 2 rows */
  .forecast-section {
    width: 100%;

    .forecast-title {
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #94a3b8;
      margin-bottom: 0.4rem;
      text-align: left;
    }

    .forecast-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr); /* 5 items per row = 2 rows for 10 items */
      gap: 6px;
      width: 100%;

      .forecast-day {
        background: #f8fafc;
        padding: 6px 2px;
        border-radius: 8px;
        border: 1px solid #e2e8f0;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3px;
        transition: transform 0.15s ease;

        &:hover {
          transform: translateY(-2px);
          border-color: #cbd5e1;
          background: #ffffff;
        }

        .day-name {
          font-size: 0.7rem;
          font-weight: 600;
          color: #475569;
        }

        i {
          font-size: 1.1rem;
        }

        .temps {
          font-size: 0.7rem;
          color: #0f172a;
          font-weight: 700;

          .temp-min {
            color: #94a3b8;
            font-weight: 500;
          }
        }
      }
    }
  }

  /* Icon Colors */
  .icon-sun { color: #d97706; }
  .icon-partly-cloudy { color: #0284c7; }
  .icon-cloud { color: #334155; }
  .icon-fog { color: #64748b; }
  .icon-rain { color: #2563eb; }
  .icon-snow { color: #38bdf8; }
  .icon-thunder { color: #7c3aed; }
}
</style>